import { inject, Injectable } from '@angular/core';
import type { TypedDocumentNode } from '@apollo/client/core';
import { catchError, map, type Observable, of } from 'rxjs';

import {
	type DesignChangeCaptureContext,
	type DesignChangeFieldPatch,
	DesignChangeRepository,
	type ProjectDesignChange,
	type ProjectDesignChangeDetail,
	type ProjectDesignChangeDocument,
	type ScopeCreepDesignChangeCards,
	type ScopeCreepDesignChangeCardsSummary,
	SELECT_GC_ERRORS,
} from '@efficiently/client-api-design-changes';
import { GraphqlExecutorService } from '@efficiently/client-core-graphql';
import type { HistoryEventChangeType } from '@efficiently/client-core-graphql-models';

import { CREATE_PROJECT_DESIGN_CHANGE_DOCUMENTS } from '../mutations/create-project-design-change-documents.mutation';
import { DELETE_PROJECT_DESIGN_CHANGE_DOCUMENT } from '../mutations/delete-project-design-change-document.mutation';
import { SUBMIT_PROJECT_DESIGN_CHANGE_TO_GC } from '../mutations/submit-project-design-change-to-gc.mutation';
import { UPDATE_PROJECT_DESIGN_CHANGE } from '../mutations/update-project-design-change.mutation';
import { DESIGN_CHANGE_CAPTURE_CONTEXT } from '../queries/design-change-capture-context.query';
import { DESIGN_CHANGE_GATED_PROJECT_FINISH_IDS } from '../queries/design-change-gated-project-finish-ids.query';
import { DESIGN_CHANGE_PENDING_PROJECT_FINISH_IDS } from '../queries/design-change-pending-project-finish-ids.query';
import { PROJECT_BID_DESIGN_CHANGE_CARDS } from '../queries/project-bid-design-change-cards.query';
import { PROJECT_BID_DESIGN_CHANGE_CARDS_SUMMARY } from '../queries/project-bid-design-change-cards-summary.query';
import { PROJECT_BID_DESIGN_CHANGE_ROWS_COUNTER } from '../queries/project-bid-design-change-rows-counter.query';
import { PROJECT_DESIGN_CHANGE } from '../queries/project-design-change.query';
import { PROJECT_DESIGN_CHANGES } from '../queries/project-design-changes.query';
import { PROJECT_DESIGN_CHANGES_PRESENCE } from '../queries/project-design-changes-presence.query';

interface DesignChangesPresenceData {
	projectDesignChanges: Array<{ _id: string }>;
}

@Injectable()
export class DesignChangeGqlRepository extends DesignChangeRepository {
	private readonly gql = inject(GraphqlExecutorService);

	public override getCaptureContext(projectFinishIds: string[]): Observable<DesignChangeCaptureContext> {
		return this.gql.query(DESIGN_CHANGE_CAPTURE_CONTEXT, {
			variables: { projectFinishIds },
			select: data => data.designChangeCaptureContext,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			skipGlobalErrorHandler: true,
		});
	}

	public override getPendingProjectFinishIds(): Observable<string[]> {
		return this.gql.query(DESIGN_CHANGE_PENDING_PROJECT_FINISH_IDS, {
			select: data => data.designChangePendingProjectFinishIds,
		});
	}

	public override getGatedProjectFinishIds(): Observable<string[]> {
		return this.gql.query(DESIGN_CHANGE_GATED_PROJECT_FINISH_IDS, {
			select: data => data.designChangeGatedProjectFinishIds,
		});
	}

	public override getDesignChanges(): Observable<ProjectDesignChange[]> {
		return this.gql.query(PROJECT_DESIGN_CHANGES, {
			select: data => data.projectDesignChanges,
		});
	}

	public override hasDesignChanges(): Observable<boolean> {
		return this.gql
			.query<DesignChangesPresenceData, Record<string, never>, DesignChangesPresenceData['projectDesignChanges']>(
				PROJECT_DESIGN_CHANGES_PRESENCE as TypedDocumentNode<DesignChangesPresenceData, Record<string, never>>,
				{
					select: data => data.projectDesignChanges,
					// eslint-disable-next-line @typescript-eslint/naming-convention
					skipGlobalErrorHandler: true,
				},
			)
			.pipe(
				map(designChanges => designChanges.length > 0),
				catchError(() => of(false)),
			);
	}

	public override getDesignChange(id: string): Observable<ProjectDesignChangeDetail | null> {
		return this.gql.query(PROJECT_DESIGN_CHANGE, {
			variables: { _id: id },
			select: data => data.projectDesignChange,
		});
	}

	public override submitToGc(id: string, gcStakeholderId: string): Observable<ProjectDesignChangeDetail> {
		return this.gql.mutate(SUBMIT_PROJECT_DESIGN_CHANGE_TO_GC, {
			variables: { _id: id, gcStakeholderId },
			expectedAppErrorCodes: SELECT_GC_ERRORS.codes,
			select: data => data.submitProjectDesignChangeToGC,
		});
	}

	public override updateDesignChange(
		id: string,
		patch: DesignChangeFieldPatch,
	): Observable<ProjectDesignChangeDetail> {
		return this.gql.mutate(UPDATE_PROJECT_DESIGN_CHANGE, {
			variables: { _id: id, input: patch },
			select: data => data.updateProjectDesignChange,
		});
	}

	public override createDocuments(id: string, contentIds: string[]): Observable<ProjectDesignChangeDocument[]> {
		return this.gql.mutate(CREATE_PROJECT_DESIGN_CHANGE_DOCUMENTS, {
			variables: { _id: id, contentIds },
			select: data => data.createProjectDesignChangeDocuments,
		});
	}

	public override deleteDocument(id: string, contentId: string): Observable<boolean> {
		return this.gql.mutate(DELETE_PROJECT_DESIGN_CHANGE_DOCUMENT, {
			variables: { _id: id, contentId },
			select: data => data.deleteProjectDesignChangeDocument,
		});
	}

	public override getScopeCreepCards(
		projectBidId: string,
		eventType: HistoryEventChangeType | null,
	): Observable<ScopeCreepDesignChangeCards> {
		return this.gql.query(PROJECT_BID_DESIGN_CHANGE_CARDS, {
			variables: { projectBidId, eventType },
			select: data => data.projectBidDesignChangeCards,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			skipGlobalErrorHandler: true,
		});
	}

	public override getScopeCreepRowsCounter(projectBidId: string): Observable<number> {
		return this.gql.query(PROJECT_BID_DESIGN_CHANGE_ROWS_COUNTER, {
			variables: { projectBidId },
			select: data => data.projectBidDesignChangeRowsCounter,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			skipGlobalErrorHandler: true,
		});
	}

	public override getScopeCreepCardsSummary(projectBidId: string): Observable<ScopeCreepDesignChangeCardsSummary> {
		return this.gql.query(PROJECT_BID_DESIGN_CHANGE_CARDS_SUMMARY, {
			variables: { projectBidId },
			select: data => data.projectBidDesignChangeCardsSummary,
			// eslint-disable-next-line @typescript-eslint/naming-convention -- `skipGlobalErrorHandler` is the executor's option name
			skipGlobalErrorHandler: true,
		});
	}
}
